<template>
    <Dialog :visible="visible" modal header="Request Withdrawal" :style="{ width: '60rem' }"
        @update:visible="$emit('update:visible', $event)">

        <div class="flex flex-col gap-4">
            <div class="card-bg rounded-lg p-4 flex flex-col items-center">
                <span class="text-sm text-gray-500">Withdrawal Amount</span>
                <span class="text-2xl font-bold mt-1">{{ formatCurrencyWithCents(currentBalance) }}</span>
            </div>

            <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">Upload Your Invoice(s) <span class="text-red-500">*</span></label>
                <p class="text-sm text-gray-400" v-if="pendingMonthsLoading">
                    Loading which months this withdrawal covers…
                </p>
                <p class="text-sm text-gray-400" v-else-if="pendingMonths.length === 0">
                    Attach one PDF invoice before submitting. The invoice date must be no later than <strong class="text-white">{{ lastMonthLabel }}</strong>.
                </p>
                <p class="text-sm text-gray-400" v-else-if="pendingMonths.length === 1">
                    Please upload <strong class="text-white">one invoice for {{ pendingMonths[0].label }}</strong> ({{ formatCurrencyWithCents(pendingMonths[0].amount) }}). The invoice date must be no later than <strong class="text-white">{{ endOfMonthLabel(pendingMonths[0]) }}</strong>.
                </p>
                <div v-else class="text-sm text-gray-400">
                    <p>Please upload <strong class="text-white">one invoice for each of the {{ pendingMonths.length }} months</strong> this withdrawal covers, dated the last day of each month:</p>
                    <ul class="mt-1 mb-1 ml-4 list-disc">
                        <li v-for="m in pendingMonths" :key="m.month" :class="isMonthCovered(m) ? 'text-green-400' : 'text-white'">
                            {{ m.label }} — {{ formatCurrencyWithCents(m.amount) }}
                            <span class="text-gray-400"> (dated {{ endOfMonthLabel(m) }})</span>
                            <i v-if="isMonthCovered(m)" class="pi pi-check ml-1 text-xs" />
                        </li>
                    </ul>
                </div>
                <FileUpload
                    ref="fileUploader"
                    mode="basic"
                    accept=".pdf"
                    :multiple="true"
                    :maxFileSize="MAX_FILE_SIZE"
                    :fileLimit="MAX_FILES"
                    :chooseLabel="selectedFiles.length > 0 ? 'Add More Invoices' : 'Choose Invoice(s)'"
                    :auto="false"
                    @select="onFileSelect"
                    class="mt-1 w-full"
                />

                <p v-if="selectedFiles.length > MAX_FILES" class="text-xs text-red-500 mt-1">
                    Maximum of {{ MAX_FILES }} invoices per request.
                </p>

                <div v-if="selectedFiles.length > 0" class="mt-2 flex flex-col gap-2">
                    <div v-for="(file, idx) in selectedFiles" :key="file.previewId"
                         class="flex items-center justify-between bg-black/20 rounded px-3 py-2">
                        <div class="flex items-center gap-2 min-w-0">
                            <i class="pi pi-file-pdf text-red-400" />
                            <span class="text-sm truncate">{{ file.name }}</span>
                            <span class="text-xs text-gray-500 whitespace-nowrap">({{ formatBytes(file.size) }})</span>
                        </div>
                        <div class="flex items-center gap-1">
                            <Button icon="pi pi-eye" text rounded size="small"
                                :title="expandedIdx === idx ? 'Hide preview' : 'Show preview'"
                                @click="togglePreview(idx)" />
                            <Button icon="pi pi-times" text rounded size="small" severity="danger"
                                title="Remove" @click="removeFile(idx)" />
                        </div>
                    </div>
                    <div v-if="expandedIdx !== null && selectedFiles[expandedIdx]?.previewUrl"
                         class="mt-1 border border-gray-600 rounded overflow-hidden" style="height: 500px;">
                        <PDF :src="selectedFiles[expandedIdx].previewUrl" style="width: 100%; height: 100%;" />
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <Button label="Cancel" severity="secondary" text @click="close" :disabled="isSubmitting" />
            <Button
                :label="submitLabel"
                icon="pi pi-send"
                severity="success"
                @click="submit"
                :disabled="!canSubmit"
                :loading="isSubmitting" />
        </template>
    </Dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import PDF from 'pdf-vue3';
import { formatCurrencyWithCents } from '@/utils/currencies';
import WalletService from '@/services/WalletService';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';

const props = defineProps({
    visible: {
        type: Boolean,
        required: true
    },
    currentBalance: {
        type: Number,
        default: 0
    }
});

const emit = defineEmits(['update:visible', 'submitted']);

const store = useStore();
const toast = useToast();

const MAX_FILE_SIZE = 5_000_000;   // 5 MB per file — matches the previous single-file cap
const MAX_FILES = 6;               // Backend body limit is 25mb; 6 × 5mb × base64 ≈ 20.5mb

const lastMonthLabel = computed(() => {
    const d = new Date();
    d.setDate(0);
    return d.toLocaleString('default', { day: 'numeric', month: 'long', year: 'numeric' });
});

// Each entry: { name, size, file (native File), previewUrl (dataURL), previewId (uuid) }
const selectedFiles = ref([]);
const expandedIdx = ref(null);
const isSubmitting = ref(false);
const fileUploader = ref(null);

// [{ month: 'YYYY-MM', label: 'May 2026', amount: N }, ...] from the backend —
// months whose payouts are still sitting in the wallet.
const pendingMonths = ref([]);
const pendingMonthsLoading = ref(false);

// Format the last day of the given pending month ({ month: 'YYYY-MM' }) as a
// human-friendly date, e.g. "31 May 2026". Passing month = m in Date.UTC(y, m, 0)
// yields the last day of the (m-1)th month because the day argument overflows
// backward from the next month's day 0.
const endOfMonthLabel = (m) => {
    if (!m?.month) return '';
    const [y, mm] = m.month.split('-').map(Number);
    const d = new Date(Date.UTC(y, mm, 0));
    return d.toLocaleString('default', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const fetchPendingMonths = async () => {
    if (!store.getters.userId) return;
    pendingMonthsLoading.value = true;
    try {
        const { data } = await WalletService.getPendingPayoutMonths(store.getters.userId);
        pendingMonths.value = Array.isArray(data?.months) ? data.months : [];
    } catch (error) {
        // Non-blocking — modal still works; message just falls back to generic copy.
        console.warn('Failed to fetch pending payout months:', error);
        pendingMonths.value = [];
    } finally {
        pendingMonthsLoading.value = false;
    }
};

// Best-effort match a pending month against an already-picked filename. Case-
// insensitive, matches "2026-05", "2026_05", "may2026", "may-2026", or "may 2026".
const isMonthCovered = (m) => {
    if (!m?.month || selectedFiles.value.length === 0) return false;
    const [year, mm] = m.month.split('-');
    const monthNames = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
    const shortName = monthNames[parseInt(mm, 10) - 1];
    const patterns = [
        new RegExp(`${year}[^0-9]?${mm}`, 'i'),           // 2026-05, 2026_05, 202605
        new RegExp(`${mm}[^0-9]?${year}`, 'i'),           // 05-2026
        new RegExp(`${shortName}[^a-z0-9]?${year}`, 'i'), // may2026, may-2026
    ];
    return selectedFiles.value.some(f => patterns.some(p => p.test(f.name)));
};

// Refetch each time the modal opens so it's always fresh.
watch(() => props.visible, (v) => {
    if (v) {
        fetchPendingMonths();
    }
}, { immediate: true });

const submitLabel = computed(() => {
    if (selectedFiles.value.length <= 1) return 'Submit Request';
    return `Submit Request (${selectedFiles.value.length} invoices)`;
});

const canSubmit = computed(() =>
    selectedFiles.value.length > 0 && selectedFiles.value.length <= MAX_FILES
);

const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const onFileSelect = (event) => {
    // PrimeVue FileUpload passes ALL selected files (across successive picks) via
    // event.files. Reconcile that against what we already have so re-picking
    // isn't duplicated.
    const incoming = Array.from(event.files || []);
    for (const file of incoming) {
        if (selectedFiles.value.some(f => f.name === file.name && f.size === file.size)) {
            continue; // dedup by (name, size)
        }
        const reader = new FileReader();
        const entry = {
            name: file.name,
            size: file.size,
            file,
            previewUrl: null,
            previewId: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        };
        reader.onload = () => { entry.previewUrl = reader.result; };
        reader.readAsDataURL(file);
        selectedFiles.value.push(entry);
    }
    // Clear PrimeVue's internal preview so the picker resets between clicks
    if (fileUploader.value?.clear) fileUploader.value.clear();
};

const removeFile = (idx) => {
    selectedFiles.value.splice(idx, 1);
    if (expandedIdx.value === idx) expandedIdx.value = null;
    else if (expandedIdx.value !== null && expandedIdx.value > idx) expandedIdx.value -= 1;
};

const togglePreview = (idx) => {
    expandedIdx.value = expandedIdx.value === idx ? null : idx;
};

const close = () => {
    selectedFiles.value = [];
    expandedIdx.value = null;
    emit('update:visible', false);
};

const submit = async () => {
    if (!canSubmit.value) return;

    isSubmitting.value = true;
    try {
        const files = selectedFiles.value.map(entry => entry.file);
        await WalletService.requestWithdrawalWithInvoices(store.getters.userId, files);
        toast.add({
            severity: 'success',
            summary: 'Withdrawal Requested',
            detail: `${files.length === 1 ? 'Invoice' : `${files.length} invoices`} received. You will receive your funds within 3 business days.`,
            life: 4000
        });
        emit('submitted');
        close();
    } catch (error) {
        console.error('Error submitting withdrawal request:', error);
        const message = error?.response?.data?.message || 'Something went wrong. Please try again.';
        toast.add({
            severity: 'error',
            summary: 'Submission Failed',
            detail: message,
            life: 4000
        });
    } finally {
        isSubmitting.value = false;
    }
};
</script>
